// src/store/index.js
import axiosInstance from "@/axiosInstance";
import { createStore } from "vuex";

const store = createStore({
  state: {
    clients: [], // inizialmente vuoto
    operators: [],
    items: [],
    quotes: [],
    operatorRole: localStorage.getItem("operatorRole") || null, // Aggiunto per memorizzare il ruolo dell'operatore
    isGoogleCalendarConnected: false,
    canConnectGoogleCalendar: false,
  },
  getters: {
    "clients/list": (state) => state.clients,
    "operators/list": (state) => state.operators,
    "operators/nonAdmin": (state) =>
      state.operators.filter((op) => !op.isAdmin),
    "items/list": (state) => state.items,
    "quotes/list": (state) => state.quotes,
    "google/calendar/connected": (state) => state.isGoogleCalendarConnected,
    "google/calendar/canConnect": (state) => state.canConnectGoogleCalendar,
  },
  mutations: {
    SET_CLIENTS(state, clients) {
      state.clients = clients;
    },
    // Nuova mutation per aggiungere al volo un cliente
    ADD_CLIENT(state, newClient) {
      state.clients.push(newClient);
    },
    // Nuova mutation per rimuovere un cliente
    REMOVE_CLIENT(state, clientId) {
      state.clients = state.clients.filter((client) => client.id !== clientId);
    },
    // Nuova mutaion per aggiornare un cliente esistente
    UPDATE_CLIENT(state, updatedClient) {
      const index = state.clients.findIndex(
        (client) => client.id === updatedClient.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.clients.splice(index, 1, updatedClient);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_OPERATOR_ROLE(state, role) {
      state.operatorRole = role;
    },

    SET_OPERATORS(state, operators) {
      state.operators = operators;
    },
    // Nuova mutation per aggiungere al volo un operatore
    ADD_OPERATOR(state, newOperator) {
      state.operators.push(newOperator);
    },
    // Nuova mutation per rimuovere un operatore
    REMOVE_OPERATOR(state, operatorId) {
      state.operators = state.operators.filter((op) => op.id !== operatorId);
    },
    ADD_OPERATOR_DOCUMENT(state, { operatorId, document }) {
      const op = state.operators.find((o) => o.id === operatorId);
      if (!op) return;
      if (!Array.isArray(op.documents)) op.documents = [];
      op.documents.push(document);
    },
    REMOVE_OPERATOR_DOCUMENT(state, { operatorId, objectName }) {
      const op = state.operators.find((o) => o.id === operatorId);
      if (!op || !op.documents) return;
      op.documents = op.documents.filter((d) => d.name !== objectName);
    },
    SET_OPERATOR_DOCUMENTS(state, { operatorId, documents }) {
      const op = state.operators.find((o) => o.id === operatorId);
      if (!op) return;
      op.documents = documents || [];
    },
    // Nuova mutaion per aggiornare un operatore esistente
    UPDATE_OPERATOR(state, updatedOperator) {
      const index = state.operators.findIndex(
        (op) => op.id === updatedOperator.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.operators.splice(index, 1, updatedOperator);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_ITEMS(state, items) {
      state.items = items;
    },
    // Nuova mutation per aggiungere un immagine a un item
    ADD_IMAGE(state, { itemId, imageUrl }) {
      const index = state.items.findIndex((item) => item.id === itemId);
      if (index !== -1) {
        state.items[index].image = imageUrl;
      }
    },
    // Nuova mutation per aggiungere al volo un item
    ADD_ITEM(state, newItem) {
      state.items.push(newItem);
    },
    // Nuova mutation per rimuovere un item
    REMOVE_ITEM(state, itemId) {
      state.items = state.items.filter((item) => item.id !== itemId);
    },
    // Nuova mutaion per aggiornare un item esistente
    UPDATE_ITEM(state, updatedItem) {
      if (!updatedItem) return;
      const index = state.items.findIndex((item) => item.id === updatedItem.id);
      if (index !== -1) {
        state.items.splice(index, 1, updatedItem);
      }
    },

    // Nuova mutation per aggiungere un documento a un item
    SET_ITEM_DOCUMENT(state, { itemId, document }) {
      const item = state.items.find((i) => i.id === itemId);
      if (!item) return;

      if (!Array.isArray(item.documents)) {
        item.documents = [];
      }

      // Conserveremo sempre un solo documento per articolo
      item.documents = [document];
    },

    // Nuova mutation per rimuovere un documento da un item
    REMOVE_ITEM_DOCUMENT(state, { itemId, objectName }) {
      const item = state.items.find((i) => i.id === itemId);
      if (!item || !item.documents) return;

      item.documents = item.documents.filter((doc) => doc.name !== objectName);
    },

    // ---------------------------------------------------------------------------//

    SET_QUOTES(state, quotes) {
      state.quotes = quotes;
    },
    // Nuova mutation per aggiungere al volo un quote
    ADD_QUOTE(state, newQuote) {
      state.quotes.push(newQuote);
    },
    // Nuova mutation per aggiungere un documento a un quote
    ADD_DOC_QUOTE(state, { quoteId, document }) {
      const index = state.quotes.findIndex((q) => q.id === quoteId);
      if (index === -1) return;

      // se non c'è ancora l'array, lo creo
      if (!Array.isArray(state.quotes[index].documents)) {
        // Vue 3: ok aggiungere una nuova proprietà reattiva
        state.quotes[index].documents = [];
      }

      state.quotes[index].documents.push(document);
    },
    // Nuova mutation per rimuovere un quote
    REMOVE_QUOTE(state, quoteId) {
      state.quotes = state.quotes.filter((quote) => quote.id !== quoteId);
    },
    // Nuova mutaion per aggiornare un quote esistente
    UPDATE_QUOTE(state, updatedQuote) {
      const index = state.quotes.findIndex(
        (quote) => quote.id === updatedQuote.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.quotes.splice(index, 1, updatedQuote);
      }
    },
    SET_GOOGLE_CALENDAR_STATUS(state, { connected, canConnect }) {
      state.isGoogleCalendarConnected = connected;
      state.canConnectGoogleCalendar = canConnect;
    },
  },

  actions: {
    async fetchClients({ commit }) {
      try {
        const response = await axiosInstance.get("/client/get-all"); // endpoint della tua API
        commit("SET_CLIENTS", response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          commit("SET_CLIENTS", []);
          return;
        }
        console.error("Errore durante il fetch dei clients:", error);
        throw error;
      }
    },
    // Nuova azione per creare un cliente
    async createClient({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("/client/add", payload);

        // commit("ADD_CLIENT", response.data.operator);
        await dispatch("fetchClients");
        return response;
      } catch (error) {
        console.error("Errore durante la creazione di un nuovo client:", error);
        throw error;
      }
    },

    // Nuova azione per aggiornare un cliente esistente
    async updateClient({ commit }, { clientId, payload }) {
      const response = await axiosInstance.patch(
        `client/update/${clientId}`,
        payload
      );
      commit("UPDATE_CLIENT", response.data.client);
      return response;
    },

    // Aggiungiamo un'azione per archiviare (o "eliminare") il cliente
    async archiveClient({ commit }, clientId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`client/archive/${clientId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_CLIENT", clientId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione di un client:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchOperators({ commit }) {
      try {
        const response = await axiosInstance.get("/auth/get-all"); // endpoint della tua API
        commit("SET_OPERATORS", response.data);
      } catch (error) {
        console.error("Errore durante il fetch degli operators:", error);
        throw error;
      }
    },

    // Nuova azione per creare un operatore
    async createOperator({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post(
          "auth/create-operator",
          payload
        );

        // commit("ADD_OPERATOR", response.data.operator);
        await dispatch("fetchOperators");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo operator:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un operatore esistente
    async updateOperator({ commit }, { operatorId, payload }) {
      const response = await axiosInstance.patch(
        `auth/update/${operatorId}`,
        payload
      );
      commit("UPDATE_OPERATOR", response.data.operator);
      return response;
    },

    // Aggiungiamo un'azione per archiviare (o "eliminare") l'operatore
    async archiveOperator({ commit }, operatorId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`auth/archive/${operatorId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_OPERATOR", operatorId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione di un operator:", error);
        throw error;
      }
    },

    async uploadOperatorDocument(
      { commit },
      { operatorId, document, filename }
    ) {
      try {
        const formData = new FormData();
        formData.append("document", document, filename);
        const response = await axiosInstance.post(
          `/upload/operators/${operatorId}/documents`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        commit("ADD_OPERATOR_DOCUMENT", {
          operatorId,
          document: response.data.document,
        });
        return response;
      } catch (error) {
        console.error(
          "Errore durante l'upload del documento operatore:",
          error
        );
        throw error;
      }
    },
    async deleteOperatorDocument({ commit }, { operatorId, objectName }) {
      try {
        const response = await axiosInstance.delete(
          `/upload/operator-documents/${objectName}`
        );
        commit("REMOVE_OPERATOR_DOCUMENT", { operatorId, objectName });
        return response;
      } catch (error) {
        console.error(
          "Errore durante l'eliminazione del documento operatore:",
          error
        );
        throw error;
      }
    },
    async fetchOperatorDocuments({ commit }, operatorId) {
      try {
        const response = await axiosInstance.get(`/auth/${operatorId}`);
        const documents = response.data.documents || [];
        commit("SET_OPERATOR_DOCUMENTS", { operatorId, documents });
      } catch (error) {
        console.error(
          "Errore durante il fetch dei documenti operatore:",
          error
        );
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchItems({ commit }) {
      try {
        const response = await axiosInstance.get("/item/get-all"); // endpoint della tua API
        commit("SET_ITEMS", response.data);
        // console.log("items: ", response);
      } catch (error) {
        console.error("Errore durante il fetch degli preventivi:", error);
        throw error;
      }
    },

    // Nuova azione per caricare un'immagine di un item
    async uploadImage({ commit }, payload) {
      try {
        if (!payload || !payload.image) {
          throw new Error("Nessuna immagine fornita");
        }
        const formData = new FormData();
        formData.append("image", payload.image, payload.image.name);
        const response = await axiosInstance.post("upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // 'response.data.url' contiene l'URL completo
        const fullUrl = response.data.url;

        // Se vuoi, aggiorna lo store con la mutation ADD_IMAGE
        // (facoltativo, se devi mostrare subito l'immagine nello store)
        commit("ADD_IMAGE", {
          itemId: payload.itemId,
          imageUrl: fullUrl,
        });

        return response;
      } catch (error) {
        console.error("Errore durante l'upload dell'immagine:", error);
        throw error;
      }
    },

    // Nuova azione per rimuovere un immagine di un item
    async removeImage({ commit }, { itemId, objectName }) {
      try {
        // Effettua una chiamata DELETE al tuo endpoint per rimuovere l'immagine
        // Qui supponiamo che l'endpoint sia "/upload/:itemId" e che il body contenga l'objectName
        const response = await axiosInstance.delete(
          `upload/${itemId}/${objectName}`
        );

        if (response.data?.item) {
          commit("UPDATE_ITEM", response.data.item);
        }
        return response;
      } catch (error) {
        console.error("Errore durante la rimozione dell'immagine:", error);
        throw error;
      }
    },

    // Nuova azione per creare un item
    async createItem({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("item/create", payload);

        // commit("ADD_ITEM", response.data.item);
        await dispatch("fetchItems");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo articolo:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un item esistente
    async updateItem({ commit }, { itemId, payload }) {
      const response = await axiosInstance.patch(
        `item/update/${itemId}`,
        payload
      );
      commit("UPDATE_ITEM", response.data.item);
      return response;
    },

    // Aggiungiamo un'azione per archiviare (o "eliminare") l'item
    async archiveItem({ commit }, itemId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`item/archive/${itemId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_ITEM", itemId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione dell'articolo:", error);
        throw error;
      }
    },

    // Aggiungiamo un'azione per caricare un documento di un item
    async uploadItemDocument({ commit }, { itemId, document, filename }) {
      try {
        const formData = new FormData();

        // Gestisci sia File che Blob
        if (document instanceof Blob) {
          formData.append("document", document, filename);
        } else {
          formData.append("document", document);
        }

        const response = await axiosInstance.post(
          `/upload/items/${itemId}/document`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        // il backend ritorna: { message, document: newDoc }
        commit("SET_ITEM_DOCUMENT", {
          itemId,
          document: response.data.document,
        });

        return response;
      } catch (error) {
        console.error("Errore durante l'upload del documento item:", error);
        throw error;
      }
    },

    async deleteItemDocument({ commit }, { itemId, objectName }) {
      try {
        const response = await axiosInstance.delete(
          `/upload/item-documents/${objectName}`
        );

        commit("REMOVE_ITEM_DOCUMENT", { itemId, objectName });

        return response;
      } catch (error) {
        console.error(
          "Errore durante l'eliminazione della scheda tecnica:",
          error
        );
        throw error;
      }
    },
    // ---------------------------------------------------------------------------//

    async fetchQuotes({ commit }) {
      try {
        const response = await axiosInstance.get("/quote/get-all");
        commit("SET_QUOTES", response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          commit("SET_QUOTES", []);
          return;
        }
        console.error("Errore durante il fetch dei preventivi:", error);
        throw error;
      }
    },

    // Nuova azione per mostrare i preventivi associati ad un operatore non admin
    async fetchOperatorQuotes({ commit }) {
      try {
        const response = await axiosInstance.get(
          "/quote/get-all-operator-quotes"
        );
        commit("SET_QUOTES", response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          commit("SET_QUOTES", []);
          return;
        }
        console.error("Errore durante il fetch dei preventivi:", error);
        throw error;
      }
    },

    // Nuova azione per creare un quote
    async createQuote({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("quote/create", payload);

        // commit("ADD_QUOTE", response.data.quote);
        await dispatch("fetchQuotes");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo preventivo:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per caricare un documento di un quote
    async uploadQuoteDocument({ commit }, { quoteId, document, filename }) {
      try {
        // Prepara il FormData
        const formData = new FormData();
        formData.append("document", document, filename);

        // POST multipart/form-data alla tua rotta
        // Nota: axiosInstance ha baseURL impostato su '/api'
        const response = await axiosInstance.post(
          `/upload/quotes/${quoteId}/documents`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        commit("ADD_DOC_QUOTE", {
          quoteId,
          document: response.data.doc, // Assicurati che il backend restituisca il documento caricato
        });
        return response;
      } catch (error) {
        console.error("Errore durante l'upload del documento:", error);
        throw error;
      }
    },

    // Nuova azione per aggiornare un quote esistente
    async updateQuote({ commit }, { quoteId, payload }) {
      const response = await axiosInstance.patch(
        `quote/update-with-delta/${quoteId}`,
        payload
      );
      commit("UPDATE_QUOTE", response.data.quote);
      return response;
    },

    // Azione per aggiungere un sub-item da parte dell'operatore
    async addSubItemByOperator(_, { quoteId, itemId, quantity, area }) {
      const response = await axiosInstance.post(
        `quote/${quoteId}/operator/add-sub-item`,
        { itemId, quantity, area }
      );
      return response;
    },

    // Azione per aggiornare le zone da parte dell'operatore
    async updateAreaByOperator(_, { quoteId, updates }) {
      const response = await axiosInstance.patch(
        `quote/${quoteId}/operator/update-area`,
        { updates }
      );
      return response;
    },

    // Aggiungiamo un'azione per archiviare (o "eliminare") l'quote
    async archiveQuote({ commit }, quoteId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`quote/archive/${quoteId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_QUOTE", quoteId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione del preventivo:", error);
        throw error;
      }
    },

    async checkGoogleCalendarStatus({ commit }) {
      try {
        const response = await axiosInstance.get("auth/google/status");
        commit("SET_GOOGLE_CALENDAR_STATUS", {
          connected: !!response.data.connected,
          canConnect: !!response.data.canConnect,
        });
      } catch {
        commit("SET_GOOGLE_CALENDAR_STATUS", {
          connected: false,
          canConnect: false,
        });
      }
    },

    async addQuoteToCalendar(_, quoteId) {
      const response = await axiosInstance.post(`calendar/quote/${quoteId}`);
      return response.data;
    },
  },
});

export default store;
